import {
  DEFAULT_ALERT_HOURS,
  END_OF_DAY,
  isAllDay,
  isWorkTime,
  nextDigestAt,
  parseHm,
  validWorkWindow,
  type AlertHours,
  type Site,
  type SiteNightContact,
} from "@fts/core";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { Note, Panel } from "../components/ui";
import type { AlertRecipientsStatus, ContactCandidate, EffectiveAlertSettings, EffectiveNightContacts, PersonName } from "../data/provider";
import { useI18n, type MessageKey } from "../i18n";
import { canEditAlertSettings, formatAlarmWhen, formatClock, formatDays, joinList, personName } from "../lib/alarms";
import { sitePath } from "../lib/fleet";
import { useAlertPeople } from "../state/alarms";
import { useSession, type TenantData } from "../state/session";

const WEEK = [1, 2, 3, 4, 5, 6, 7] as const;

type Loaded = {
  /** effective_alert_settings(site): the database resolves inheritance, also through ancestors the user cannot see. */
  effective: EffectiveAlertSettings | null;
  /** effective_night_contacts(site): resolved by the database exactly like the notification trigger. */
  night: EffectiveNightContacts;
  /** Night-contact rows of the sites the user can see (RLS); used for the site's OWN list only. */
  contacts: SiteNightContact[];
  /** alert_contact_candidates(site); null below fleet manager (or when it failed). */
  candidates: ContactCandidate[] | null;
  /** site_alert_recipients_status(site): how many people an alarm would reach (counts only). */
  recipients: AlertRecipientsStatus | null;
  loaded: boolean;
  error: boolean;
};
type Modal = "hours" | "resetHours" | "night" | "resetNight" | null;

/** Re-render every minute so the "right now" preview stays current. */
function useNow(stepMs = 60_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const h = setInterval(() => setNow(new Date()), stepMs);
    return () => clearInterval(h);
  }, [stepMs]);
  return now;
}

/**
 * "Bildirim ayarları" of a site: working hours and night contacts (own or inherited from the nearest
 * parent site; ADR 0011) and a plain-language preview of who would be notified right now.
 * Visibility follows the site; changes need fleet manager or above.
 */
export function SiteAlertsPanel({ data, site }: { data: TenantData; site: Site }) {
  const { t, locale } = useI18n();
  const { provider, tenant, role, user } = useSession();
  const { people } = useAlertPeople();
  const [state, setState] = useState<Loaded>({
    effective: null,
    night: { contacts: [], sourceSiteId: null },
    contacts: [],
    candidates: null,
    recipients: null,
    loaded: false,
    error: false,
  });
  const [modal, setModal] = useState<Modal>(null);
  const now = useNow();
  const tenantId = tenant?.id;
  const canEdit = canEditAlertSettings(role);

  const reload = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [effective, night, contacts, candidates, recipients] = await Promise.all([
        provider.getEffectiveAlertSettings(site.id),
        provider.getEffectiveNightContacts(site.id),
        provider.listNightContacts(tenantId),
        canEdit ? provider.listContactCandidates(site.id).catch(() => null) : Promise.resolve(null),
        // Required: the preview must never claim a recipient the database would not notify.
        provider.getAlertRecipientsStatus(site.id),
      ]);
      setState({ effective, night, contacts, candidates, recipients, loaded: true, error: false });
    } catch {
      setState((s) => ({ ...s, loaded: true, error: true }));
    }
  }, [provider, tenantId, site.id, canEdit]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const hours = useMemo(() => {
    const e = state.effective;
    const sourceSiteId = e?.sourceSiteId ?? null;
    return { hours: e?.hours ?? DEFAULT_ALERT_HOURS, sourceSiteId, own: sourceSiteId === site.id };
  }, [state.effective, site.id]);
  // The database resolves inheritance (also through parents the user cannot see) and reachability.
  const night = useMemo(() => {
    const sourceSiteId = state.night.sourceSiteId;
    return { userIds: state.night.contacts.map((c) => c.userId), sourceSiteId, own: sourceSiteId === site.id };
  }, [state.night, site.id]);
  // The site's own rows (what the editor replaces), even when they do not apply (e.g. nobody reachable).
  const ownIds = useMemo(() => [...new Set(state.contacts.filter((c) => c.siteId === site.id).map((c) => c.userId))], [state.contacts, site.id]);
  // Names: the user's own profile (admins: every member), the candidates RPC for fleet managers and the
  // night contacts' names from effective_night_contacts.
  const known = useMemo<PersonName[]>(() => {
    const out: PersonName[] = [...people];
    for (const p of [...(state.candidates ?? []), ...state.night.contacts]) if (!out.some((x) => x.userId === p.userId)) out.push(p);
    return out;
  }, [people, state.candidates, state.night]);
  const sourceLabel = (id: string) =>
    data.sites.some((s) => s.id === id)
      ? t("siteAlerts.inherited", { site: sitePath(data.sites, id).map((s) => s.name).join(" / ") })
      : t("siteAlerts.inheritedHidden");
  const name = (id: string) => personName(t, known, id, user?.id);
  const hasParent = !!site.parentId && data.sites.some((s) => s.id === site.parentId);

  if (!state.loaded)
    return (
      <Panel title={t("siteAlerts.title")}>
        <div className="loading" role="status">
          <span className="spinner" aria-hidden="true" />
          {t("common.loading")}
        </div>
      </Panel>
    );
  if (state.error)
    return (
      <Panel title={t("siteAlerts.title")}>
        <p className="form-error" role="alert">
          {t("siteAlerts.loadError")}
        </p>
      </Panel>
    );

  const hoursSource = hours.own ? t("siteAlerts.own") : hours.sourceSiteId ? sourceLabel(hours.sourceSiteId) : t("siteAlerts.hours.default");

  return (
    <Panel title={t("siteAlerts.title")}>
      <section className="alert-section" aria-labelledby="alerts-hours">
        <div className="alert-section-head">
          <h3 id="alerts-hours" className="alert-section-title">
            {t("siteAlerts.hours.title")}
          </h3>
          <span className={`badge ${hours.own ? "tone-accent" : ""}`}>{hoursSource}</span>
        </div>
        <p className="alert-value">
          {isAllDay(hours.hours)
            ? t("siteAlerts.hours.summaryAllDay", { days: formatDays(t, hours.hours.workDays) })
            : t("siteAlerts.hours.summary", { days: formatDays(t, hours.hours.workDays), start: hours.hours.workStart, end: hours.hours.workEnd })}
        </p>
        <p className="field-hint">{t("siteAlerts.hours.tz")}</p>
        {canEdit && (
          <div className="alert-actions">
            <button type="button" className="button button-secondary button-sm" onClick={() => setModal("hours")}>
              <Icon name={hours.own ? "edit" : "plus"} size={15} />
              {hours.own ? t("siteAlerts.hours.edit") : t("siteAlerts.hours.override")}
            </button>
            {hours.own && (
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal("resetHours")}>
                <Icon name="refresh" size={15} />
                {hasParent ? t("siteAlerts.hours.resetParent") : t("siteAlerts.hours.resetDefault")}
              </button>
            )}
          </div>
        )}
      </section>

      <section className="alert-section" aria-labelledby="alerts-night">
        <div className="alert-section-head">
          <h3 id="alerts-night" className="alert-section-title">
            {t("siteAlerts.night.title")}
          </h3>
          {night.userIds.length > 0 && (
            <span className={`badge ${night.own ? "tone-accent" : ""}`}>
              {night.own ? t("siteAlerts.own") : sourceLabel(night.sourceSiteId!)}
            </span>
          )}
        </div>
        <p className="field-hint">{t("siteAlerts.night.hint")}</p>
        {night.userIds.length > 0 ? (
          <p className="alert-value">{joinList(night.userIds.map(name), locale)}</p>
        ) : (
          <p className="alert-value muted">{t("siteAlerts.night.none")}</p>
        )}
        {canEdit && (
          <div className="alert-actions">
            <button type="button" className="button button-secondary button-sm" onClick={() => setModal("night")}>
              <Icon name="edit" size={15} />
              {t("siteAlerts.night.edit")}
            </button>
            {ownIds.length > 0 && (
              <button type="button" className="button button-secondary button-sm" onClick={() => setModal("resetNight")}>
                <Icon name="refresh" size={15} />
                {t("siteAlerts.night.reset")}
              </button>
            )}
          </div>
        )}
      </section>

      <section className="alert-section" aria-labelledby="alerts-preview">
        <div className="alert-section-head">
          <h3 id="alerts-preview" className="alert-section-title">
            {t("siteAlerts.preview.title")}
          </h3>
        </div>
        <AlertPreview
          people={known}
          candidates={state.candidates}
          recipients={state.recipients ?? { dayRecipients: 0, nightRecipients: 0, adminsFallback: true, adminCopies: 0 }}
          hours={hours.hours}
          nightIds={night.userIds}
          now={now}
        />
      </section>

      {!canEdit && <p className="panel-footnote">{t("siteAlerts.readOnly")}</p>}

      {modal === "hours" && tenantId && (
        <HoursDialog
          site={site}
          tenantId={tenantId}
          initial={hours.hours}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await reload();
          }}
        />
      )}
      {modal === "resetHours" && (
        <ConfirmDialog
          title={t("siteAlerts.hours.resetTitle")}
          confirmLabel={hasParent ? t("siteAlerts.hours.resetParent") : t("siteAlerts.hours.resetDefault")}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await provider.resetAlertSettings(site.id);
            setModal(null);
            await reload();
          }}
        >
          {t("siteAlerts.hours.resetBody", { site: site.name })}
        </ConfirmDialog>
      )}
      {modal === "night" && tenantId && (
        <NightDialog
          site={site}
          tenantId={tenantId}
          people={known}
          candidates={state.candidates ?? []}
          own={ownIds}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            await reload();
          }}
        />
      )}
      {modal === "resetNight" && tenantId && (
        <ConfirmDialog
          title={t("siteAlerts.night.resetTitle")}
          confirmLabel={t("siteAlerts.night.reset")}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            await provider.setNightContacts(tenantId, site.id, []);
            setModal(null);
            await reload();
          }}
        >
          {t("siteAlerts.night.resetBody", { site: site.name })}
        </ConfirmDialog>
      )}
    </Panel>
  );
}

/**
 * Who gets what if an alarm happened now, in plain language. The counts come from the database
 * (site_alert_recipients_status, resolved like the notification trigger), so a role nobody holds is
 * never named and a site whose alarms would reach nobody gets a red warning.
 */
function AlertPreview({
  people,
  candidates,
  recipients,
  hours,
  nightIds,
  now,
}: {
  people: PersonName[];
  candidates: ContactCandidate[] | null;
  recipients: AlertRecipientsStatus;
  hours: AlertHours;
  nightIds: string[];
  now: Date;
}) {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const name = (id: string) => personName(t, people, id, user?.id);
  const dayCount = recipients.dayRecipients;
  const reachesNobody = dayCount === 0 && recipients.nightRecipients === 0;
  // Admins with an e-mail who get a copy of every alarm (tenants.alarm_copy_admins), counted by the database.
  const adminCopies = recipients.adminCopies;
  const copyAdmins = adminCopies > 0;
  // Candidates (fleet manager+) are exactly the members who see the site: name the day recipients from the
  // group the database uses. Below fleet manager there is no list, and the group is described with its size.
  const dayGroupRoles = copyAdmins ? ["safety", "fleet_manager", "admin"] : recipients.adminsFallback ? ["admin"] : ["safety", "fleet_manager"];
  const dayIds = candidates ? candidates.filter((c) => dayGroupRoles.includes(c.role)).map((c) => c.userId) : [];
  const dayText =
    dayIds.length > 0
      ? joinList(dayIds.map(name), locale)
      : recipients.adminsFallback
        ? dayCount === 1
          ? t("siteAlerts.preview.adminsFallbackOne")
          : t("siteAlerts.preview.adminsFallback", { count: dayCount })
        : copyAdmins
          ? dayCount === 1
            ? t("siteAlerts.preview.dayGroupWithAdminsOne")
            : t("siteAlerts.preview.dayGroupWithAdmins", { count: dayCount })
          : dayCount === 1
            ? t("siteAlerts.preview.dayGroupOne")
            : t("siteAlerts.preview.dayGroup", { count: dayCount });
  // Same rules as routeAlarm (core): in hours -> day recipients; off hours -> night contacts, else day recipients.
  const working = isWorkTime(hours, now);
  const digest = nextDigestAt(hours, now);
  const when = digest ? formatAlarmWhen(digest, locale) : null;
  const clock = formatClock(now, locale);
  const lines: string[] = [];

  lines.push(working ? t("siteAlerts.preview.inHours", { time: clock }) : t("siteAlerts.preview.offHours", { time: clock }));
  if (working) {
    if (dayCount > 0) {
      lines.push(t("siteAlerts.preview.impactNow", { who: dayText }));
      lines.push(t("siteAlerts.preview.othersNow"));
    } else {
      lines.push(t("siteAlerts.preview.impactNobody"));
      lines.push(t("siteAlerts.preview.othersNobody"));
    }
  } else {
    if (nightIds.length === 0) {
      if (dayCount > 0) {
        lines.push(t("siteAlerts.preview.impactNow", { who: dayText }));
        lines.push(t("siteAlerts.preview.impactFallback"));
      } else {
        lines.push(t("siteAlerts.preview.impactNobody"));
      }
    } else {
      lines.push(t("siteAlerts.preview.impactNow", { who: joinList(nightIds.map(name), locale) }));
      if (copyAdmins)
        lines.push(adminCopies === 1 ? t("siteAlerts.preview.impactAdminsTooOne") : t("siteAlerts.preview.impactAdminsToo", { count: adminCopies }));
      if (when && dayCount > 0) lines.push(t("siteAlerts.preview.impactDigest", { when }));
    }
    lines.push(
      !when
        ? t("siteAlerts.preview.noDigest")
        : dayCount > 0
          ? t("siteAlerts.preview.othersDigest", { when, who: dayText })
          : t("siteAlerts.preview.othersDigestNobody", { when }),
    );
  }
  if (copyAdmins) lines.push(t("siteAlerts.preview.adminsCopy"));

  return (
    <>
      {reachesNobody && (
        <p className="warning-banner tone-crit" role="alert">
          <Icon name="alert" size={16} />
          <span>{t("siteAlerts.preview.noRecipients")}</span>
        </p>
      )}
      <ul className="alert-preview" aria-live="polite">
        {lines.map((l, i) => (
          <li key={i}>{l}</li>
        ))}
      </ul>
      <div className="alert-actions">
        <Note>{t("alarm.noBlock")}</Note>
      </div>
    </>
  );
}

function HoursDialog({
  site,
  tenantId,
  initial,
  onClose,
  onSaved,
}: {
  site: Site;
  tenantId: string;
  initial: { workDays: number[]; workStart: string; workEnd: string };
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [days, setDays] = useState<number[]>(initial.workDays);
  const [allDay, setAllDay] = useState(isAllDay(initial));
  const [start, setStart] = useState(isAllDay(initial) ? DEFAULT_ALERT_HOURS.workStart : initial.workStart);
  // A time input cannot show 24:00: an end of day is edited through "All day".
  const [end, setEnd] = useState(isAllDay(initial) || parseHm(initial.workEnd) === 1440 ? DEFAULT_ALERT_HOURS.workEnd : initial.workEnd);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const daysError = touched && days.length === 0 ? t("siteAlerts.hours.daysRequired") : null;
  const startError = !allDay && touched && Number.isNaN(parseHm(start)) ? t("form.required") : null;
  const endError = allDay
    ? null
    : touched && Number.isNaN(parseHm(end))
      ? t("form.required")
      : touched && !Number.isNaN(parseHm(start)) && !validWorkWindow(start, end)
        ? t("siteAlerts.hours.endAfterStart")
        : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    const workStart = allDay ? "00:00" : start.slice(0, 5);
    const workEnd = allDay ? END_OF_DAY : end.slice(0, 5);
    if (days.length === 0 || !validWorkWindow(workStart, workEnd)) return;
    setBusy(true);
    setError(null);
    try {
      await provider.saveAlertSettings({ tenantId, siteId: site.id, workDays: days, workStart, workEnd });
      await onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("siteAlerts.hours.dialogTitle", { site: site.name })}
      description={t("siteAlerts.hours.childNote")}
      onClose={() => !busy && onClose()}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <fieldset className="fieldset" aria-describedby={daysError ? "hours-days-error" : undefined}>
        <legend>{t("siteAlerts.hours.days")}</legend>
        <div className="day-picker">
          {WEEK.map((d) => (
            <label key={d} className="check-row">
              <input
                type="checkbox"
                checked={days.includes(d)}
                onChange={(e) => setDays((prev) => (e.target.checked ? [...prev, d].sort((a, b) => a - b) : prev.filter((x) => x !== d)))}
              />
              <span>{t(`day.long.${d}` as MessageKey)}</span>
            </label>
          ))}
        </div>
        {daysError && (
          <p id="hours-days-error" className="field-error">
            {daysError}
          </p>
        )}
      </fieldset>
      <label className="check-row">
        <input type="checkbox" role="switch" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} aria-describedby="hours-allday-hint" />
        <span>
          <span className="strong">{t("siteAlerts.hours.allDay")}</span>
          <span id="hours-allday-hint" className="field-hint">
            {t("siteAlerts.hours.allDayHint")}
          </span>
        </span>
      </label>
      {!allDay && (
        <div className="time-row">
          <FormField id="hours-start" label={t("siteAlerts.hours.start")} error={startError}>
            <input {...fieldAria("hours-start", { error: startError })} type="time" step={300} value={start} onChange={(e) => setStart(e.target.value)} />
          </FormField>
          <FormField id="hours-end" label={t("siteAlerts.hours.end")} error={endError}>
            <input {...fieldAria("hours-end", { error: endError })} type="time" step={300} value={end} onChange={(e) => setEnd(e.target.value)} />
          </FormField>
        </div>
      )}
      <p className="field-hint">{t("siteAlerts.hours.tz")}</p>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

function NightDialog({
  site,
  tenantId,
  people,
  candidates,
  own,
  onClose,
  onSaved,
}: {
  site: Site;
  tenantId: string;
  people: PersonName[];
  /** alert_contact_candidates(site). */
  candidates: ContactCandidate[];
  /** The site's own contacts (empty when it inherits). */
  own: string[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useI18n();
  const { provider, user } = useSession();
  const [picked, setPicked] = useState<string[]>(own);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Own contacts the user cannot see by name stay selectable, so saving never drops them silently.
  const options = useMemo(() => {
    const ids = candidates.map((c) => c.userId);
    return [...ids, ...own.filter((u) => !ids.includes(u))];
  }, [candidates, own]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await provider.setNightContacts(tenantId, site.id, picked);
      await onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("siteAlerts.night.dialogTitle", { site: site.name })}
      description={t("siteAlerts.night.hint")}
      onClose={() => !busy && onClose()}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <fieldset className="fieldset">
        <legend>{t("siteAlerts.night.pick")}</legend>
        {options.length === 0 ? (
          <p className="field-hint">{t("siteAlerts.night.noCandidates")}</p>
        ) : (
          <div className="site-checklist">
            {options.map((id) => {
              const p = candidates.find((x) => x.userId === id);
              return (
                <label key={id} className="check-row">
                  <input
                    type="checkbox"
                    checked={picked.includes(id)}
                    onChange={(e) => setPicked((prev) => (e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)))}
                  />
                  <span>
                    {personName(t, people, id, user?.id)}
                    {p && <span className="muted small">{t(`role.${p.role}`)}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        )}
        <p className="field-hint">{t("siteAlerts.night.pickHint")}</p>
      </fieldset>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}
